import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol.js';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol.js';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol.js';
import type { LegendSwatchKind } from '@/map/layers/types';

export type RGB = [number, number, number];

export type ClassificationSymbol =
  | SimpleFillSymbol
  | SimpleLineSymbol
  | SimpleMarkerSymbol;

export function symbolFor(
  geometryType: string | undefined,
  rgb: RGB,
): ClassificationSymbol | undefined {
  const [r, g, b] = rgb;
  switch (geometryType) {
    case 'polygon':
    case 'esriGeometryPolygon':
      return new SimpleFillSymbol({
        color: [r, g, b, 0.4],
        outline: { color: [r, g, b, 0.95], width: 0.8 },
      });
    case 'polyline':
    case 'esriGeometryPolyline':
      return new SimpleLineSymbol({ color: [r, g, b, 0.95], width: 1.75 });
    case 'point':
    case 'multipoint':
    case 'esriGeometryPoint':
    case 'esriGeometryMultipoint':
      return new SimpleMarkerSymbol({
        style: 'circle',
        color: [r, g, b, 0.85],
        size: 7,
        outline: { color: [244, 236, 221, 1], width: 1 },
      });
    default:
      return undefined;
  }
}

export function geometrySwatchKind(
  geometryType: string | undefined,
): LegendSwatchKind | undefined {
  switch (geometryType) {
    case 'polygon':
    case 'esriGeometryPolygon':
      return 'fill';
    case 'polyline':
    case 'esriGeometryPolyline':
      return 'line';
    case 'point':
    case 'multipoint':
    case 'esriGeometryPoint':
    case 'esriGeometryMultipoint':
      return 'point';
    default:
      return undefined;
  }
}

export function rgbToHex([r, g, b]: RGB): string {
  const h = (n: number) =>
    Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
  return `#${h(r)}${h(g)}${h(b)}`;
}

const NAMED_COLORS: Record<string, RGB> = {
  red: [220, 53, 69],
  crimson: [220, 20, 60],
  orange: [232, 127, 59],
  amber: [240, 175, 60],
  yellow: [240, 200, 60],
  green: [38, 168, 100],
  lime: [120, 200, 90],
  teal: [33, 160, 162],
  cyan: [60, 180, 220],
  blue: [78, 113, 200],
  navy: [40, 60, 130],
  indigo: [75, 0, 130],
  purple: [156, 96, 196],
  violet: [156, 96, 196],
  magenta: [220, 60, 200],
  pink: [240, 130, 180],
  brown: [140, 90, 60],
  black: [30, 30, 30],
  white: [240, 240, 240],
  gray: [150, 150, 150],
  grey: [150, 150, 150],
};

/** Accept hex (`#rrggbb` / `rrggbb`) or a small set of named colors. Returns null on failure. */
export function parseColor(input: string): RGB | null {
  const text = input.trim().toLowerCase();
  if (!text) return null;
  const named = NAMED_COLORS[text];
  if (named) return [...named];
  const hex = /^#?([0-9a-f]{6})$/.exec(text);
  if (hex) {
    const n = parseInt(hex[1], 16);
    return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
  }
  const short = /^#?([0-9a-f]{3})$/.exec(text);
  if (short) {
    const r = parseInt(short[1][0] + short[1][0], 16);
    const g = parseInt(short[1][1] + short[1][1], 16);
    const b = parseInt(short[1][2] + short[1][2], 16);
    return [r, g, b];
  }
  return null;
}
